import { MAX_CHANGE_LOG_LIMIT, MAX_SLOT_CANDIDATES_PER_PAGE, } from "@repo/contract/training";
import { progressKeys } from "../progress/keys";
import { addExclusion, addPlanItem, addPlanSession, discardDraft, fetchActivePlan, fetchChanges, fetchDraftDiff, fetchDraftPlan, fetchExclusions, fetchPlanItemAlternatives, fetchPlanItemCandidates, fetchSessionCandidates, generatePlan, movePlanItem, publishPlan, rejectDraftChange, removeExclusion, removePlanItem, removePlanSession, renamePlanSession, reorderPlanSessions, replacePlanItem, restartPlanWeeks, restorePlanItem, retargetPlanItem, setPlanSuperset, updatePlanSession, } from "./api";
import { changeKeys, pendingKeys, planKeys } from "./keys";
/**
 * The plan domain as option objects (`../core/options.ts`). The app spreads
 * them into its hooks (T11); nothing here knows about React or TanStack.
 *
 * ── WHAT EACH WRITE INVALIDATES, AND WHY NOT SIMPLY `["plan"]` ──────────────
 * Only `publish` swaps the whole plan, so only `publish` invalidates every
 * plan read. An item write changes the week (active and draft) and the
 * account's history, but NOT the candidate feed a deck is dealing from:
 * refetching an infinite query refetches every page it holds, and the pool is
 * re-ranked between requests, so the card under the person's thumb would
 * change. The refusal list is the opposite case — it is read by every
 * candidate and alternative query, so its writes invalidate exactly those.
 */
export function activePlanOptions(api, options = {}) {
    return {
        queryFn: ({ signal }) => fetchActivePlan(api, options, { signal }),
        queryKey: planKeys.active(options.locale),
    };
}
export function draftPlanOptions(api, options = {}) {
    return {
        queryFn: ({ signal }) => fetchDraftPlan(api, options, { signal }),
        queryKey: planKeys.draft(options.locale),
    };
}
/**
 * What the draft changes (T32) — the Today banner's counts and the draft
 * sheet's list. It fails with `plan.no_draft` when there is no draft; the
 * screen reads that as "nothing to review", never as a reason to generate.
 */
export function draftDiffOptions(api, options = {}) {
    return {
        queryFn: ({ signal }) => fetchDraftDiff(api, options, { signal }),
        queryKey: planKeys.draftDiff(options.locale),
    };
}
/**
 * What a draft write reaches: the diff and the draft read (both under
 * `["plan", "draft"]`), the pending signals whose `draft` it may change, and
 * the account's history. Not the plan in effect — neither write touches it.
 */
const DRAFT_WRITES = [
    planKeys.draftAll,
    pendingKeys.all,
    changeKeys.all,
];
/** Say no to one change; the answer is the recomputed diff. */
export function rejectDraftChangeMutation(api) {
    return {
        invalidates: DRAFT_WRITES,
        mutationFn: (input) => rejectDraftChange(api, input),
        mutationKey: ["plan", "draft", "reject"],
    };
}
/** Throw the draft away. `GET /me/plan?draft=1` answers `null` after it. */
export function discardDraftMutation(api) {
    return {
        invalidates: DRAFT_WRITES,
        mutationFn: () => discardDraft(api),
        mutationKey: ["plan", "draft", "discard"],
    };
}
/**
 * The draft generator. **Only the draft review screen (T59) uses it**
 * (`D150`, `T-145`): see {@link generatePlan}. The locale travels with the
 * input so the returned draft's names match the screen's language.
 */
export function generatePlanMutation(api) {
    return {
        invalidates: [planKeys.draftAll, changeKeys.all],
        mutationFn: (input) => generatePlan(api, input),
        mutationKey: ["plan", "generate"],
    };
}
export function publishPlanMutation(api) {
    return {
        invalidates: [planKeys.all, changeKeys.all, progressKeys.all],
        mutationFn: (input) => publishPlan(api, input),
        mutationKey: ["plan", "publish"],
    };
}
/**
 * What an item or session write reaches. Besides the week and the history:
 *
 * - `["progress"]`, because `GET /me/progress` reads the plan's `targetSets`,
 *   and without it the Progress screen showed the old plan after an edit.
 * - the session feed of what may be ADDED, because it leaves out what the
 *   session already shows: an exercise just added would otherwise still be
 *   offered there, and adding it twice is one tap. It is one page, not a
 *   feed under the person's thumb, so a refetch re-deals nothing mid-swipe —
 *   the reason the slot feed stays out of this list does not apply to it.
 */
const PLAN_WEEK_WRITES = [
    planKeys.activeAll,
    planKeys.draftAll,
    planKeys.sessionCandidatesAll,
    changeKeys.all,
    progressKeys.all,
];
export function addPlanItemMutation(api) {
    return {
        invalidates: PLAN_WEEK_WRITES,
        mutationFn: (input) => addPlanItem(api, input),
        mutationKey: ["plan", "items", "add"],
    };
}
export function removePlanItemMutation(api) {
    return {
        invalidates: PLAN_WEEK_WRITES,
        mutationFn: ({ itemId }) => removePlanItem(api, itemId),
        mutationKey: ["plan", "items", "remove"],
    };
}
/**
 * The deck's "swap for the plan": one call, the new item in the old one's slot
 * (`replacePlanItem`). It refreshes the plan in effect AND the draft (the item
 * may be in either) and the change history — not the candidate feed the deck
 * is dealing, for the reason every item write gives.
 */
export function replacePlanItemMutation(api) {
    return {
        invalidates: PLAN_WEEK_WRITES,
        mutationFn: (input) => replacePlanItem(api, input),
        mutationKey: ["plan", "items", "replace"],
    };
}
/**
 * The undo of a remove or a replace (`restorePlanItem`): the same row comes
 * back, so it invalidates exactly what the write it undoes did.
 */
export function restorePlanItemMutation(api) {
    return {
        invalidates: PLAN_WEEK_WRITES,
        mutationFn: (input) => restorePlanItem(api, input),
        mutationKey: ["plan", "items", "restore"],
    };
}
/** Week 1 starts now (`D198`): every week the screens show moves. */
export function restartPlanWeeksMutation(api) {
    return {
        invalidates: [planKeys.all, changeKeys.all, progressKeys.all],
        mutationFn: ({ planId }) => restartPlanWeeks(api, planId),
        mutationKey: ["plan", "restart-weeks"],
    };
}
/** A new day, at the end of the plan (`D197`). */
export function addPlanSessionMutation(api) {
    return {
        invalidates: PLAN_WEEK_WRITES,
        mutationFn: (input) => addPlanSession(api, input),
        mutationKey: ["plan", "sessions", "add"],
    };
}
/** A day and its lines, off the plan (`D197`). */
export function removePlanSessionMutation(api) {
    return {
        invalidates: PLAN_WEEK_WRITES,
        mutationFn: ({ sessionId }) => removePlanSession(api, sessionId),
        mutationKey: ["plan", "sessions", "remove"],
    };
}
/** One line to another day of the plan (`D214`). */
export function movePlanItemMutation(api) {
    return {
        invalidates: PLAN_WEEK_WRITES,
        mutationFn: (input) => movePlanItem(api, input),
        mutationKey: ["plan", "items", "move"],
    };
}
/** The days of the plan in a new order (`D210`). */
export function reorderPlanSessionsMutation(api) {
    return {
        invalidates: PLAN_WEEK_WRITES,
        mutationFn: (input) => reorderPlanSessions(api, input),
        mutationKey: ["plan", "sessions", "reorder"],
    };
}
/** Lines grouped as a superset, or ungrouped (`D246`). */
export function setPlanSupersetMutation(api) {
    return {
        invalidates: PLAN_WEEK_WRITES,
        mutationFn: (input) => setPlanSuperset(api, input),
        mutationKey: ["plan", "superset"],
    };
}
/** The person's own numbers for one line (`D197`). */
export function retargetPlanItemMutation(api) {
    return {
        invalidates: PLAN_WEEK_WRITES,
        mutationFn: (input) => retargetPlanItem(api, input),
        mutationKey: ["plan", "items", "retarget"],
    };
}
/** Label, weekday, and the item order of one session. */
export function updatePlanSessionMutation(api) {
    return {
        invalidates: PLAN_WEEK_WRITES,
        mutationFn: (input) => updatePlanSession(api, input),
        mutationKey: ["plan", "sessions", "update"],
    };
}
/**
 * The plan header's rename (`T141`). It invalidates the week like every other
 * session write, and NOTHING here writes the cache by hand: an empty `name`
 * makes the server store a regenerated default the app cannot compute, so the
 * only way to learn the new name is to read it back.
 */
export function renamePlanSessionMutation(api) {
    return {
        invalidates: PLAN_WEEK_WRITES,
        mutationFn: (input) => renamePlanSession(api, input),
        mutationKey: ["plan", "sessions", "rename"],
    };
}
export function planItemAlternativesOptions(api, itemId, options = {}) {
    return {
        queryFn: ({ signal }) => fetchPlanItemAlternatives(api, itemId, options, { signal }),
        queryKey: planKeys.alternatives(itemId, options.locale),
    };
}
/**
 * The first page of an item's candidates as a PLAIN query — what the picker
 * shows (`D181`): one page, compared side by side, never dealt a card at a
 * time. `nextCursor` is ignored here; the catalogue is the way past it.
 */
export function slotCandidatesPageOptions(api, itemId, options = {}) {
    const limit = candidateLimit(options.limit);
    return {
        queryFn: ({ signal }) => fetchPlanItemCandidates(api, itemId, { limit, locale: options.locale }, { signal }),
        queryKey: planKeys.candidatesPage(itemId, options.locale, limit),
    };
}
/**
 * The first page of what may be ADDED to a session for one muscle, a plain
 * query like {@link slotCandidatesPageOptions} (`D182`).
 */
export function sessionCandidatesOptions(api, sessionId, options) {
    const limit = candidateLimit(options.limit);
    return {
        queryFn: ({ signal }) => fetchSessionCandidates(api, sessionId, { limit, locale: options.locale, muscleId: options.muscleId }, { signal }),
        queryKey: planKeys.sessionCandidates(sessionId, options.muscleId, options.locale, limit),
    };
}
/** Clamped into the contract's 1…`MAX_SLOT_CANDIDATES_PER_PAGE`, so the key
 *  names the page the server will actually answer. */
function candidateLimit(limit) {
    return limit === undefined
        ? undefined
        : Math.min(Math.max(1, limit), MAX_SLOT_CANDIDATES_PER_PAGE);
}
export function exclusionsOptions(api, options = {}) {
    return {
        queryFn: ({ signal }) => fetchExclusions(api, options, { signal }),
        queryKey: planKeys.exclusions(options.locale),
    };
}
const EXCLUSION_WRITES = [
    planKeys.exclusionsAll,
    planKeys.candidatesAll,
    planKeys.sessionCandidatesAll,
    planKeys.alternativesAll,
];
/** The answer says whether this call made the exclusion (`created`), which
 *  is what an undo reads before removing it. */
export function addExclusionMutation(api) {
    return {
        invalidates: EXCLUSION_WRITES,
        mutationFn: (input) => addExclusion(api, input),
        mutationKey: ["plan", "exclusions", "add"],
    };
}
export function removeExclusionMutation(api) {
    return {
        invalidates: EXCLUSION_WRITES,
        mutationFn: ({ exerciseId }) => removeExclusion(api, exerciseId),
        mutationKey: ["plan", "exclusions", "remove"],
    };
}
/**
 * The account's history (`GET /me/changes`), paged on the opaque
 * `nextBefore`. `limit` is clamped to `MAX_CHANGE_LOG_LIMIT`.
 */
export function changesInfiniteOptions(api, options = {}) {
    const limit = options.limit === undefined
        ? undefined
        : Math.min(Math.max(1, options.limit), MAX_CHANGE_LOG_LIMIT);
    return {
        getNextPageParam: (lastPage) => lastPage.nextBefore,
        initialPageParam: null,
        queryFn: ({ pageParam, signal }) => fetchChanges(api, { before: pageParam ?? undefined, limit, locale: options.locale }, { signal }),
        queryKey: changeKeys.list(limit, options.locale),
    };
}
//# sourceMappingURL=options.js.map